<script lang="ts" setup>
// -------------------------------
// 🔹 Imports Vue & composables
// -------------------------------
import { ref, toRaw } from 'vue';

// -------------------------------
// 🔹 Composants
// -------------------------------
import TextInput from '../common/BaseInput.vue';
import BaseButtonPrimary from '../common/BaseButtonPrimary.vue';
import { IconUploadCloud } from '@/assets/svg/common.svg';
// -------------------------------
// 🔹 Stores ou mutations
// -------------------------------
import { OnboardingMutation } from '@/mutations/onboarding.mutation';
import { useCommerceStore } from '@/store/commerce.store';
import { useQuery } from '@tanstack/vue-query';
import {
  errorMessage,
  fileInputOnboarding,
  formOnboardingImage,
  idFetauredUpta,
  idToAddValue,
  isImageUploadidOnboarding,
  isLoadingFetauredUptad,
  isUpdatedOrderOnboard,
  mediaId,
  previewUrlOnboard,
} from '@/refs/commerce.refs';
import {
  isLoaderLoginFormActivated,
  isLoaderLoginFormActivatedOnboarding,
} from '@/refs/login.form.refs';
const {
  handleCreateOnboardingImage,
  handleUpdateOnboardingImage,
  handleUpdateOnboardingOrder,
} = OnboardingMutation();
import { useDialogStore } from '@/store/dialog.store';
import { cleanBody } from '@/utils/cleanBody';
// -------------------------------
// 🔹 Refs / données
// -------------------------------

const commerceStore = useCommerceStore();

const emit = defineEmits<{
  (e: 'HandleClose'): void;
}>();
const {
  data: dataGetCommerces,
  isLoading: isLoadingGetCommerces,
  isError: isErrorGetCommerces,
  refetch: refectGetCommerces,
} = useQuery({
  queryKey: ['GetCommerces'],
  queryFn: () => commerceStore.getCommerces(),
});

const handleClose = (): void => {
  emit('HandleClose');
};

const dialogStore = useDialogStore();
const triggerUpload = () => {
  fileInputOnboarding.value?.click();
};

const handleFileChange = (event: Event) => {
  const file = (event.target as HTMLInputElement).files?.[0];
  previewUrlOnboard.value = URL.createObjectURL(file);
  if (!file) return;

  if (file.size > 5 * 1024 * 1024) {
    errorMessage.value = 'La taille maximale autorisée est de 5 Mo.';
    return;
  }

  formOnboardingImage.value.file = (event.target as HTMLInputElement)
    .files?.[0];
};

const handleDelete = () => {
  previewUrlOnboard.value = null;

  if (fileInputOnboarding.value) fileInputOnboarding.value.value = '';
};

const createCommerceSlug = async () => {
  isLoaderLoginFormActivatedOnboarding.value =
    !isLoaderLoginFormActivatedOnboarding.value;
  try {
    const res = await handleCreateOnboardingImage({
      restaurantId: formOnboardingImage.value.restaurantId,
      role: formOnboardingImage.value.role,
      displayOrder: Number(formOnboardingImage.value.displayOrder),
      startDate: String(formOnboardingImage.value.startDate),
      endDate: String(formOnboardingImage.value.endDate),
      isActive: Boolean(formOnboardingImage.value.isActive),
      file: formOnboardingImage.value.file,
      metadata: {
        title: formOnboardingImage.value.metadata.title,
        description: formOnboardingImage.value.metadata.description,
        slideDuration: Number(formOnboardingImage.value.metadata.slideDuration),
        skipAllowed: Boolean(formOnboardingImage.value.metadata.skipAllowed),
      },
    });

    if (res) {
      setTimeout(() => {
        dialogStore.closeDialog();
        isLoaderLoginFormActivatedOnboarding.value =
          !isLoaderLoginFormActivatedOnboarding.value;
      }, 1000);
    }
  } catch (error) {
    setTimeout(() => {
      isLoaderLoginFormActivatedOnboarding.value =
        !isLoaderLoginFormActivatedOnboarding.value;
    }, 1000);

    alert('error from server' + JSON.stringify(error));
  }
};

const updateECommerceSlugOnboarding = async () => {
  isLoaderLoginFormActivatedOnboarding.value = true; // active le loader
  try {
    const payload = cleanBody({
      startDate: formOnboardingImage.value.startDate
        ? String(formOnboardingImage.value.startDate)
        : null,
      endDate: formOnboardingImage.value.endDate
        ? String(formOnboardingImage.value.endDate)
        : null,

      isActive: Boolean(formOnboardingImage.value.isActive),
      title: formOnboardingImage.value.metadata.title,
      description: formOnboardingImage.value.metadata.description,
      slideDuration: Number(formOnboardingImage.value.metadata.slideDuration),
      skipAllowed: Boolean(formOnboardingImage.value.metadata.skipAllowed),
    });

    const res = await handleUpdateOnboardingImage(payload);

    if (res) {
      setTimeout(() => {
        dialogStore.closeDialog();
        isLoaderLoginFormActivatedOnboarding.value = false; // désactive le loader
      }, 1000);
    }
  } catch (error) {
    setTimeout(() => {
      isLoaderLoginFormActivatedOnboarding.value = false; // désactive le loader
    }, 1000);

    console.error('Error from server:', error);
    alert('Error from server: ' + JSON.stringify(error));
  }
};

const updateECommerceSlugOnboardingOrder = async () => {
  isLoaderLoginFormActivatedOnboarding.value = true; // active le loader
  try {
    const payload = cleanBody({
      mediaId: String(idFetauredUpta.value),
      displayOrder: String(formOnboardingImage.value.displayOrder),
    });

    const res = await handleUpdateOnboardingOrder({
      data: [
        {
          mediaId: String(idFetauredUpta.value),
          displayOrder: Number(formOnboardingImage.value.displayOrder),
        },
      ],
    });

    if (res) {
      setTimeout(() => {
        dialogStore.closeDialog();
        isLoaderLoginFormActivatedOnboarding.value = false; // désactive le loader
      }, 1000);
    }
  } catch (error) {
    setTimeout(() => {
      isLoaderLoginFormActivatedOnboarding.value = false; // désactive le loader
    }, 1000);

    console.error('Error from server:', error);
    alert('Error from server: ' + JSON.stringify(error));
  }
};

const handleSubmit = () => {
  if (isUpdatedOrderOnboard.value) {
    updateECommerceSlugOnboardingOrder();
  } else {
    if (isLoadingFetauredUptad.value) {
      updateECommerceSlugOnboarding();
    } else {
      createCommerceSlug();
    }
  }
};
</script>

<template>
  <form
    @submit.prevent="handleSubmit"
    class="mx-auto max-w-lg space-y-6 rounded-xl bg-white p-4 shadow-md"
  >
    <!-- Role -->
    <div v-show="!isUpdatedOrderOnboard">
      <label for="role" class="mb-1 block font-medium text-gray-700"
        >Rôle de l'image</label
      >
      <select
        v-model="formOnboardingImage.role"
        id="role"
        class="w-full rounded-md border px-3 py-2 focus:ring-2 focus:ring-green-500"
      >
        <option value="onboarding_image">Onboarding</option>
        <option value="banner">Banner</option>
        <option value="story">Story</option>
        <option value="product_highlight">Product Highlight</option>
      </select>
    </div>

    <!-- Ordre d'affichage -->
    <TextInput
      v-model="formOnboardingImage.displayOrder"
      name="displayOrder"
      type="text"
      label="Ordre d'affichage"
      placeholder="Entrez l'ordre d'affichage"
      required
    />

    <!-- Dates -->
    <div class="flex gap-4" v-show="!isUpdatedOrderOnboard">
      <TextInput
        v-model="formOnboardingImage.startDate"
        name="startDate"
        type="date"
        label="Date de début"
      />
      <TextInput
        v-model="formOnboardingImage.endDate"
        name="endDate"
        type="date"
        label="Date de fin"
      />
    </div>

    <!-- Active -->
    <div class="flex items-center gap-2" v-show="!isUpdatedOrderOnboard">
      <input
        type="checkbox"
        v-model="formOnboardingImage.isActive"
        id="isActive"
        class="h-4 w-4 rounded text-green-600"
      />
      <label for="isActive" class="text-gray-700">Active</label>
    </div>

    <!-- Metadata -->
    <TextInput
      v-show="!isUpdatedOrderOnboard"
      v-model="formOnboardingImage.metadata.title"
      name="title"
      label="Titre"
      placeholder="Titre de la slide"
    />
    <TextInput
      v-show="!isUpdatedOrderOnboard"
      v-model="formOnboardingImage.metadata.description"
      name="description"
      label="Description"
      placeholder="Description (max 300 caractères)"
    />
    <TextInput
      v-show="!isUpdatedOrderOnboard"
      v-model="formOnboardingImage.metadata.slideDuration"
      name="slideDuration"
      type="text"
      label="Durée de la slide (ms)"
      placeholder="5000"
    />
    <div class="flex items-center gap-2" v-show="!isUpdatedOrderOnboard">
      <input
        type="checkbox"
        v-model="formOnboardingImage.metadata.skipAllowed"
        id="skipAllowed"
        class="h-4 w-4 rounded text-green-600"
      />
      <label for="skipAllowed" class="text-gray-700"
        >Autoriser à passer la slide</label
      >
    </div>

    <!-- Upload moderne -->
    <div v-show="!isUpdatedOrderOnboard">
      <div
        v-show="!isLoadingFetauredUptad"
        class="mx-auto flex w-full max-w-sm flex-col items-center gap-4"
      >
        <div
          v-if="!previewUrlOnboard"
          class="border-border-input flex w-full cursor-pointer flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed bg-white px-10 py-8 text-center transition hover:bg-gray-50"
          @click="triggerUpload"
        >
          <div
            v-html="IconUploadCloud"
            class="flex items-center justify-center"
          />

          <div class="space-y-1 text-sm text-gray-600">
            <p>Format : <span class="font-medium">PNG, JPEG, JPG</span></p>
            <p>Taille max : <span class="font-medium">5 Mo</span></p>
            <p>
              Résolution conseillée :
              <span class="font-medium">500 × 500 px</span>
            </p>
          </div>

          <span
            class="cursor-pointer text-sm text-indigo-600 underline hover:text-indigo-800"
          >
            Charger un fichier
          </span>

          <input
            ref="fileInputOnboarding"
            type="file"
            accept="image/png, image/jpeg, image/jpg"
            class="hidden"
            @change="handleFileChange"
          />
        </div>

        <div
          v-else
          class="border-border-input relative h-48 w-full overflow-hidden rounded-2xl border border-dashed bg-white shadow-md"
        >
          <img
            :src="previewUrlOnboard"
            alt="Aperçu du logo"
            class="h-full w-full object-cover"
          />

          <!-- Bouton supprimer -->
          <button
            @click="handleDelete"
            class="absolute top-2 right-2 rounded bg-red-100 px-2 py-1 text-xs text-red-600 transition hover:bg-red-600 hover:text-white"
          >
            Supprimer
          </button>
        </div>
        <p v-if="errorMessage" class="text-center text-sm text-red-600">
          {{ errorMessage }}
        </p>
      </div>
    </div>

    <div v-show="!isUpdatedOrderOnboard">
      <div v-show="!isLoadingFetauredUptad">
        <label for="commerce" class="mb-1 block font-medium text-gray-700"
          >Sélectionnez le commerce<span class="text-red-500">*</span></label
        >
        <select
          id="commerce"
          v-model="formOnboardingImage.restaurantId"
          class="w-full rounded-xl border px-3 py-2 focus:ring-2 focus:ring-green-500"
        >
          <option value="" disabled>Veuillez sélectionner un commerce</option>
          <option
            v-for="value in dataGetCommerces?.data"
            :key="value.id"
            :value="value.id"
          >
            {{ value.designation }}
          </option>
        </select>
      </div>
    </div>

    <div class="h-8" />

    <div class="fixed bottom-0 left-0 w-full bg-white py-6">
      <div class="flex flex-row items-center justify-between gap-8 px-[16px]">
        <div class="w-full">
          <BaseButtonPrimary
            :isClicked="isLoaderLoginFormActivatedOnboarding"
            type="submit"
            :label="isImageUploadidOnboarding ? 'Modifier' : 'Enregistrer'"
            style_of_btn="primary_color_of_btn "
            class="w-full text-white"
          />
        </div>

        <div class="w-full" @click="handleClose">
          <BaseButtonPrimary
            type="button"
            :label="'Annuler'"
            style_of_btn="secondary_color_of_btn "
            class="text-grand-text w-full"
          />
        </div>
      </div>
    </div>
  </form>
</template>
